import { Reveal } from "../components/Reveal";
import SectionNav from "../components/SectionNav";
import { PageHero, SectionHead, CtaBand } from "../components/ui";
import { CaesDiagram, DiabaticAdiabatic } from "../components/CaesDiagrams";
import { IMAGES } from "../data/site";

const TECH_SECTIONS = [
    { id: "position", label: "Position" },
    { id: "two-technologies", label: "Technologies" },
    { id: "how-caes-works", label: "CAES process" },
    { id: "caes-variants", label: "Variants" },
];

const TECH = [
    {
        t: "BESS",
        full: "Battery Energy Storage Systems",
        rows: ["Shorter-duration flexibility", "Fast response", "Grid services"],
        note: "Deployed where speed and shorter-duration balancing define value.",
    },
    {
        t: "CAES",
        full: "Compressed Air Energy Storage",
        rows: ["Long-duration storage", "Multi-hour to multi-day capability", "System resilience"],
        note: "Deployed where scale and duration define value.",
    },
];

export default function Technology() {
    return (
        <>
            <PageHero
                eyebrow="Technology"
                lines={["Proven technology,", "disciplined deployment."]}
                sub="AddiGen is not a technology developer. We select and deploy proven storage technologies where the energy system needs them."
                trail={[{ label: "Home", path: "/" }, { label: "Technology" }]}
                image={IMAGES.plantNight}
                imageAlt="Industrial energy infrastructure at night"
            />
            <SectionNav items={TECH_SECTIONS} navKey="tech" />
            <section id="position" className="bg-paper">
                <div className="container-x grid gap-10 py-20 sm:py-24 lg:grid-cols-12 lg:py-28">
                    <div className="lg:col-span-4">
                        <Reveal><p className="eyebrow lg:sticky lg:top-36">Our position</p></Reveal>
                    </div>
                    <div className="space-y-8 lg:col-span-8">
                        <Reveal>
                            <p className="font-display text-2xl font-medium leading-[1.35] text-ink sm:text-3xl">
                                The technology is proven. The discipline is in where, and how,
                                it is deployed.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="body-lg">
                                AddiGen does not develop technology. We apply two complementary,
                                proven storage technologies — CAES and BESS — based on duration,
                                grid need and market requirements.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>
            <section id="two-technologies" className="border-y border-line bg-white">
                <div className="container-x py-20 sm:py-24 lg:py-28">
                    <SectionHead eyebrow="Two technologies" title="A complementary pair, not a preference." />
                    <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-8">
                        {TECH.map((x, i) => (
                            <Reveal key={x.t} delay={i * 0.1}>
                                <article className="flex h-full flex-col border border-line bg-paper p-8 sm:p-10" data-testid={`tech-panel-${x.t.toLowerCase()}`}>
                                    <div className="flex items-baseline justify-between">
                                        <p className="stat-numeral text-5xl sm:text-6xl">{x.t}</p>
                                        <p className="max-w-[10rem] text-right font-mono text-[10px] uppercase tracking-[0.16em] text-ink-3">{x.full}</p>
                                    </div>
                                    <ul className="mt-10 flex-1 space-y-0 divide-y divide-line border-y border-line">
                                        {x.rows.map((r) => (
                                            <li key={r} className="flex items-baseline gap-4 py-4">
                                                <span aria-hidden="true" className="mt-2 block h-px w-6 shrink-0 bg-blue" />
                                                <span className="font-display text-lg font-semibold text-ink">{r}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="body-sm mt-6">{x.note}</p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="body-sm mt-8 max-w-2xl">
                            Together they cover the flexibility spectrum: batteries respond in
                            seconds for grid services; CAES shifts large volumes of energy across
                            hours and days.
                        </p>
                    </Reveal>
                </div>
            </section>
            <section id="how-caes-works" className="bg-navy text-white">
                <div className="container-x py-20 sm:py-24 lg:py-32">
                    <SectionHead
                        dark
                        eyebrow="How CAES works"
                        title="Compress. Store. Expand. Return."
                        intro="Electricity from renewables compresses air into storage — typically underground. When the grid needs power, the air is released through an expansion stage and electricity returns to the grid."
                    />
                    <div className="mt-16">
                        <CaesDiagram dark />
                    </div>
                </div>
            </section>
            <section id="caes-variants" className="bg-paper">
                <div className="container-x py-20 sm:py-24 lg:py-28">
                    <SectionHead
                        eyebrow="Two CAES configurations"
                        title="What happens to the heat defines the variant."
                    />
                    <div className="mt-14">
                        <DiabaticAdiabatic />
                    </div>
                </div>
            </section>
            <CtaBand />
        </>
    );
}
